import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Close,
  CurrencyRupee,
  PersonSearch,
  Print,
  ReceiptLong,
  Search,
} from '@mui/icons-material';
import { feePaymentAPI } from '../../services/api';

const today = () => new Date().toISOString().slice(0, 10);

const initialPaymentForm = {
  fee_type: '',
  amount: '',
  payment_mode: 'CASH',
  payment_date: today(),
  transaction_id: '',
  remarks: '',
};

const currency = (value) => Number(value || 0).toFixed(2);

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const ReceiptPreview = ({ open, payment, student, summary, onClose }) => {
  const receiptRef = useRef(null);

  const handlePrint = () => {
    if (!receiptRef.current) return;

    const printWindow = window.open('', '_blank', 'width=720,height=900');
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Fee Receipt - ${payment?.receipt_no || ''}</title>
          <style>
            * { box-sizing: border-box; }
            body { margin: 0; padding: 24px; font-family: Arial, sans-serif; color: #0f172a; background: #ffffff; }
            .receipt-print { max-width: 680px; margin: 0 auto; }
            @media print {
              body { padding: 0; }
              .receipt-print { max-width: none; }
            }
          </style>
        </head>
        <body>
          <div class="receipt-print">${receiptRef.current.innerHTML}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const amount = currency(payment?.amount);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Receipt Preview
        <IconButton onClick={onClose} size="small" aria-label="Close receipt preview">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          ref={receiptRef}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 3,
            bgcolor: '#fff',
          }}
        >
          <Box sx={{ textAlign: 'center', borderBottom: '2px solid #0f172a', pb: 2, mb: 2.5 }}>
            <Typography variant="h5" fontWeight={900}>
              Basaveswhara lower primery school
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Belur, Chikkanasabi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fee Payment Receipt
            </Typography>
          </Box>

          <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
            {[
              ['Receipt No.', payment?.receipt_no || '-'],
              ['Date', formatDate(payment?.payment_date)],
              ['Mode', payment?.payment_mode || '-'],
            ].map(([label, value]) => (
              <Grid item xs={4} key={label}>
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1, p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={800}>
                    {value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mb: 2.5 }}>
            <Typography variant="overline" color="text.secondary" fontWeight={800}>
              Student Details
            </Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography fontWeight={900}>{student?.student_name || '-'}</Typography>
              <Typography variant="body2" color="text.secondary">
                Enrollment: {student?.enrollment_no || '-'} | Class {student?.standard || '-'}
                {student?.section ? `-${student.section}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Parent: {student?.father_name || '-'} | Phone: {student?.parent_phone || '-'}
              </Typography>
            </Box>
          </Box>

          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fee Head</TableCell>
                  <TableCell>Transaction</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{payment?.fee_type || 'General'}</TableCell>
                  <TableCell>{payment?.transaction_id || '-'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    INR {amount}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={2} sx={{ fontWeight: 900 }}>
                    Total Paid
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 900 }}>
                    INR {amount}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {payment?.remarks && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Remarks:</strong> {payment.remarks}
            </Typography>
          )}

          {summary && (
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {[
                ['Total Fees', summary.total_fee],
                ['Total Paid', summary.total_paid],
                ['Balance Due', summary.balance],
              ].map(([label, value]) => (
                <Grid item xs={4} key={label}>
                  <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1, p: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      {label}
                    </Typography>
                    <Typography variant="body2" fontWeight={800}>
                      INR {currency(value)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mt: 5 }}>
            <Typography variant="caption" color="text.secondary">
              This is a computer-generated receipt.
            </Typography>
            <Box sx={{ textAlign: 'center', minWidth: 140 }}>
              <Box sx={{ borderTop: '1px solid #0f172a', pt: 0.75 }}>
                <Typography variant="caption">Authorized Signature</Typography>
              </Box>
            </Box>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<Print />} onClick={handlePrint}>
          Print Receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FeePayments = () => {
  const [search, setSearch] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [feeDetails, setFeeDetails] = useState(null);
  const [paymentForm, setPaymentForm] = useState(initialPaymentForm);
  const [searching, setSearching] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [receiptPayment, setReceiptPayment] = useState(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (search.trim().length < 2) {
        setStudents([]);
        return;
      }

      try {
        setSearching(true);
        setError('');
        const response = await feePaymentAPI.searchStudents(search.trim());
        setStudents(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to search students');
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const loadStudentFees = async (studentId, options = {}) => {
    try {
      setLoadingDetails(true);
      setError('');
      if (!options.keepSuccess) {
        setSuccess('');
      }
      setSelectedStudentId(studentId);
      const response = await feePaymentAPI.getStudentFees(studentId);
      setFeeDetails(response.data);
      const firstBalance = response.data?.fee_items?.find((item) => Number(item.balance) > 0);
      setPaymentForm({
        ...initialPaymentForm,
        fee_type: firstBalance?.fee_type || response.data?.fee_items?.[0]?.fee_type || 'General',
        amount: firstBalance?.balance ? String(firstBalance.balance) : '',
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load student fee details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handlePaymentChange = (event) => {
    const { name, value } = event.target;
    setPaymentForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'fee_type') {
      const feeItem = feeDetails?.fee_items?.find((item) => item.fee_type === value);
      if (feeItem && Number(feeItem.balance) > 0) {
        setPaymentForm((prev) => ({
          ...prev,
          fee_type: value,
          amount: String(feeItem.balance),
        }));
      }
    }
  };

  const handleCollectPayment = async (event) => {
    event.preventDefault();

    if (!selectedStudentId) {
      setError('Select a student first.');
      return;
    }
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) {
      setError('Enter a valid payment amount.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const response = await feePaymentAPI.collectPayment({
        ...paymentForm,
        student_id: selectedStudentId,
      });
      const savedPayment = {
        ...paymentForm,
        id: response.data.id,
        receipt_no: response.data.receipt_no,
        amount: Number(paymentForm.amount),
      };
      await loadStudentFees(selectedStudentId, { keepSuccess: true });
      setReceiptPayment(savedPayment);
      setSuccess(`Payment saved. Receipt: ${response.data.receipt_no}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to collect payment');
    } finally {
      setSaving(false);
    }
  };

  const selectedStudent = feeDetails?.student;
  const feeOptions = useMemo(() => {
    const items = feeDetails?.fee_items || [];
    return items.length > 0 ? items : [{ fee_type: 'General', balance: 0 }];
  }, [feeDetails]);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Fee Payments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Search a student, review balances, and collect payments.
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <TextField
              label="Search student"
              placeholder="Name, enrollment no, phone"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} />
                  </InputAdornment>
                ) : null,
              }}
            />

            <Box sx={{ mt: 2 }}>
              {students.length > 0 ? (
                <List disablePadding>
                  {students.map((student) => (
                    <ListItemButton
                      key={student.id}
                      selected={selectedStudentId === student.id}
                      onClick={() => loadStudentFees(student.id)}
                      sx={{ borderRadius: 1, mb: 1 }}
                    >
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5 }}>
                        {student.student_name?.charAt(0)}
                      </Avatar>
                      <ListItemText
                        primary={student.student_name}
                        secondary={`${student.enrollment_no} - Class ${student.standard}${student.section ? `-${student.section}` : ''}`}
                      />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Stack alignItems="center" spacing={1.5} sx={{ py: 6, color: 'text.secondary' }}>
                  <PersonSearch sx={{ fontSize: 44 }} />
                  <Typography variant="body2" textAlign="center">
                    Type at least 2 characters to find a student.
                  </Typography>
                </Stack>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={8}>
          {loadingDetails ? (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            </Paper>
          ) : selectedStudent ? (
            <Stack spacing={3}>
              <Paper sx={{ p: 3 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  spacing={2}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                      {selectedStudent.student_name?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{selectedStudent.student_name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedStudent.enrollment_no} - Class {selectedStudent.standard}
                        {selectedStudent.section ? `-${selectedStudent.section}` : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Parent: {selectedStudent.father_name || '-'} - {selectedStudent.parent_phone || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Chip
                      icon={<AccountBalanceWallet />}
                      label={`Total: ${currency(feeDetails.summary.total_fee)}`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={`Paid: ${currency(feeDetails.summary.total_paid)}`}
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      label={`Balance: ${currency(feeDetails.summary.balance)}`}
                      color={Number(feeDetails.summary.balance) > 0 ? 'warning' : 'success'}
                    />
                  </Stack>
                </Stack>
              </Paper>

              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <Paper sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>
                      Balance Details
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Fee</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Fee</TableCell>
                            <TableCell align="right">Paid</TableCell>
                            <TableCell align="right">Balance</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {feeDetails.fee_items.length > 0 ? (
                            feeDetails.fee_items.map((item) => (
                              <TableRow key={`${item.fee_source}-${item.id}`} hover>
                                <TableCell>{item.fee_type}</TableCell>
                                <TableCell>
                                  <Chip size="small" label={item.fee_source} variant="outlined" />
                                </TableCell>
                                <TableCell align="right">{currency(item.amount)}</TableCell>
                                <TableCell align="right">{currency(item.paid)}</TableCell>
                                <TableCell align="right">{currency(item.balance)}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                No fees assigned yet.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Paper sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>
                      💳 Collect Payment
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <strong>Fee vs Payment:</strong> Fees are set during Student Approval. This form records PAYMENT collection only.
                    </Alert>
                    <Box component="form" onSubmit={handleCollectPayment}>
                      <Stack spacing={2}>
                        <TextField
                          select
                          name="fee_type"
                          label="Fee Head"
                          value={paymentForm.fee_type}
                          onChange={handlePaymentChange}
                          fullWidth
                        >
                          {feeOptions.map((item) => (
                            <MenuItem key={item.fee_type} value={item.fee_type}>
                              {item.fee_type} {Number(item.balance) > 0 ? `- Bal ${currency(item.balance)}` : ''}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          name="amount"
                          label="Amount"
                          type="number"
                          value={paymentForm.amount}
                          onChange={handlePaymentChange}
                          required
                          fullWidth
                          inputProps={{ min: 1, step: '0.01' }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CurrencyRupee fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          select
                          name="payment_mode"
                          label="Payment Mode"
                          value={paymentForm.payment_mode}
                          onChange={handlePaymentChange}
                          required
                          fullWidth
                        >
                          <MenuItem value="CASH">Cash</MenuItem>
                          <MenuItem value="UPI">UPI</MenuItem>
                          <MenuItem value="CARD">Card</MenuItem>
                          <MenuItem value="ONLINE">Online</MenuItem>
                          <MenuItem value="CHEQUE">Cheque</MenuItem>
                        </TextField>
                        <TextField
                          name="payment_date"
                          label="Payment Date"
                          type="date"
                          value={paymentForm.payment_date}
                          onChange={handlePaymentChange}
                          required
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          name="transaction_id"
                          label="Transaction / Cheque No"
                          value={paymentForm.transaction_id}
                          onChange={handlePaymentChange}
                          fullWidth
                        />
                        <TextField
                          name="remarks"
                          label="Remarks"
                          value={paymentForm.remarks}
                          onChange={handlePaymentChange}
                          fullWidth
                          multiline
                          minRows={2}
                        />
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={<ReceiptLong />}
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : 'Save Payment'}
                        </Button>
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <Paper sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6">Recent Paid Details</Typography>
                  <Chip icon={<ReceiptLong />} label={`${feeDetails.payments.length} receipts`} variant="outlined" />
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Receipt</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Fee Head</TableCell>
                        <TableCell>Mode</TableCell>
                        <TableCell>Transaction</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {feeDetails.payments.length > 0 ? (
                        feeDetails.payments.map((payment) => (
                          <TableRow key={payment.id} hover>
                            <TableCell>{payment.receipt_no}</TableCell>
                            <TableCell>{new Date(payment.payment_date).toLocaleDateString()}</TableCell>
                            <TableCell>{payment.fee_type || 'General'}</TableCell>
                            <TableCell>
                              <Chip size="small" label={payment.payment_mode} />
                            </TableCell>
                            <TableCell>{payment.transaction_id || '-'}</TableCell>
                            <TableCell align="right">{currency(payment.amount)}</TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                startIcon={<Print />}
                                onClick={() => setReceiptPayment(payment)}
                              >
                                Print
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No payments collected yet.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          ) : (
            <Paper sx={{ p: 3 }}>
              <Stack alignItems="center" spacing={2} sx={{ py: 10, color: 'text.secondary' }}>
                <ReceiptLong sx={{ fontSize: 56 }} />
                <Typography variant="h6">Select a student to view fee details</Typography>
                <Typography variant="body2">
                  Balance, recent payments, and collection form will appear here.
                </Typography>
              </Stack>
            </Paper>
          )}
        </Grid>
      </Grid>

      <ReceiptPreview
        open={Boolean(receiptPayment)}
        payment={receiptPayment}
        student={selectedStudent}
        summary={feeDetails?.summary}
        onClose={() => setReceiptPayment(null)}
      />
    </Box>
  );
};

export default FeePayments;
